import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge, Empty } from "@/components/admin/Ui";
import { BookOpen, Plus, Trash, ArrowClockwise, FloppyDisk, Prohibit, Notepad, Detective, Calendar } from "@phosphor-icons/react";

const SCOPES = [
  { v: "all", l: "All agents" },
  { v: "outreach", l: "Outreach" },
  { v: "content", l: "Content" },
  { v: "event_invitation", l: "Event invitations" },
  { v: "reply_classify", l: "Replies" },
  { v: "onboarding", l: "Onboarding" },
];

const VERDICTS = [
  { v: "borrow", l: "Borrow" },
  { v: "avoid", l: "Avoid" },
];

const ACTIVITY_STATUSES = [
  { v: "active", l: "Active" },
  { v: "planned", l: "Planned" },
  { v: "considered", l: "Considered" },
  { v: "avoid", l: "Avoid" },
];

const ACTIVITY_CATEGORIES = [
  "Membership Programs",
  "Meetings",
  "Exhibitions & Trade Shows",
  "Broadcasts",
  "Outreach",
  "Member Services",
  "Content",
];

const SEVERITIES = [
  { v: "hard", l: "Hard (never use)" },
  { v: "soft", l: "Soft (use sparingly)" },
];

const inputCls = "w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy";
const selectCls = inputCls + " bg-white";

export default function Playbook() {
  const [pb, setPb] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("banned");

  const load = async () => {
    const { data } = await api.get("/playbook");
    setPb(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!pb) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/playbook", {
        banned_phrases: pb.banned_phrases || [],
        writing_rules: pb.writing_rules || [],
        competitor_insights: pb.competitor_insights || [],
        activities: pb.activities || [],
        custom_notes: pb.custom_notes || "",
      });
      setPb(data);
      toast.success("Playbook saved — agents will use this on the next run.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Restore the shipped default playbook? Your custom edits will be overwritten.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/playbook/reset");
      setPb(data);
      toast.success("Default playbook restored");
    } catch (e) {
      toast.error("Reset failed");
    } finally { setBusy(false); }
  };

  if (!pb) return <div className="p-8 text-navy/60" data-testid="admin-playbook-loading">Loading playbook…</div>;

  const banned = pb.banned_phrases || [];
  const rules = pb.writing_rules || [];
  const insights = pb.competitor_insights || [];
  const activities = pb.activities || [];

  const updateList = (key, idx, patch) => {
    const list = [...(pb[key] || [])];
    list[idx] = { ...list[idx], ...patch };
    setPb({ ...pb, [key]: list });
  };
  const removeAt = (key, idx) => {
    const list = [...(pb[key] || [])];
    list.splice(idx, 1);
    setPb({ ...pb, [key]: list });
  };
  const addBanned = () => setPb({ ...pb, banned_phrases: [...banned, { phrase: "", severity: "hard", note: "" }] });
  const addRule = () => setPb({ ...pb, writing_rules: [...rules, { rule: "", scope: "all" }] });
  const addInsight = () => setPb({ ...pb, competitor_insights: [...insights, { competitor: "", pattern: "", verdict: "avoid" }] });
  const addActivity = () => setPb({ ...pb, activities: [...activities, { category: "Meetings", name: "", cadence: "", status: "considered", description: "", source: "", epas_notes: "" }] });

  const tabs = [
    { k: "banned", l: "Banned phrases", n: banned.length, icon: Prohibit },
    { k: "rules", l: "Writing rules", n: rules.length, icon: Notepad },
    { k: "insights", l: "Competitor intel", n: insights.length, icon: Detective },
    { k: "activities", l: "Activity catalog", n: activities.length, icon: Calendar },
    { k: "notes", l: "Operator notes", n: (pb.custom_notes || "").length, icon: BookOpen },
  ];

  return (
    <div data-testid="admin-playbook-page">
      <PageHeader
        title="Playbook"
        subtitle="Prompt library · Competitor anti-patterns · Editorial rules"
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={resetDefaults}
              disabled={busy}
              data-testid="playbook-reset-button"
              className="inline-flex items-center gap-2 border border-black/15 bg-white text-navy px-4 py-2 text-sm hover:bg-navy/5 transition-colors disabled:opacity-50"
            >
              <ArrowClockwise size={16} /> Reset defaults
            </button>
            <button
              onClick={save}
              disabled={busy}
              data-testid="playbook-save-button"
              className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 text-sm hover:bg-navy/90 transition-colors disabled:opacity-50"
            >
              <FloppyDisk size={16} /> {busy ? "Saving…" : "Save playbook"}
            </button>
          </div>
        }
      />

      <div className="px-8 pt-6">
        <div className="bg-white border border-black/10 p-4 text-sm text-navy/70 flex items-start gap-3">
          <BookOpen size={20} weight="duotone" className="text-ember flex-shrink-0 mt-0.5" />
          <div>
            The playbook is <b>live</b>. When you save, every drafting agent (Outreach, Content, Event Invitations, Replies, Onboarding) will pick it up on their very next run.
            Rules can be scoped to specific agents. Insights marked <Badge tone="green">borrow</Badge> tell the LLM which competitor moves to imitate; <Badge tone="red">avoid</Badge> tells it what never to do.
          </div>
        </div>
      </div>

      <div className="px-8 pt-6">
        <div className="flex flex-wrap gap-2 border-b border-black/10">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`playbook-tab-${t.k}`}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  active ? "border-ember text-navy" : "border-transparent text-navy/60 hover:text-navy"
                }`}
              >
                <Icon size={16} /> {t.l}
                <span className="text-[10px] tabular-nums bg-navy/5 text-navy/60 px-1.5 py-0.5 rounded-sm">{t.n}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-8 space-y-6">
        {tab === "banned" && (
          <div className="space-y-3" data-testid="playbook-banned-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-navy/70">Phrases the LLM must never emit. Hard = strict ban. Soft = allowed only with a very specific reason.</p>
              <button onClick={addBanned} data-testid="playbook-add-banned" className="inline-flex items-center gap-2 bg-ember text-white px-3 py-1.5 text-xs hover:bg-ember/90">
                <Plus size={14} /> Add phrase
              </button>
            </div>
            {banned.length === 0 && <Empty label="No banned phrases yet — click Add phrase" />}
            {banned.map((b, idx) => (
              <div key={idx} className="bg-white border border-black/10 p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                <input
                  data-testid={`playbook-banned-phrase-${idx}`}
                  className={inputCls + " md:col-span-5"}
                  value={b.phrase || ""}
                  onChange={(e) => updateList("banned_phrases", idx, { phrase: e.target.value })}
                  placeholder='e.g. "hope this email finds you well"'
                />
                <select
                  data-testid={`playbook-banned-severity-${idx}`}
                  className={selectCls + " md:col-span-2"}
                  value={b.severity || "hard"}
                  onChange={(e) => updateList("banned_phrases", idx, { severity: e.target.value })}
                >
                  {SEVERITIES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
                <input
                  data-testid={`playbook-banned-note-${idx}`}
                  className={inputCls + " md:col-span-4"}
                  value={b.note || ""}
                  onChange={(e) => updateList("banned_phrases", idx, { note: e.target.value })}
                  placeholder="Why is this banned (context for future you)"
                />
                <button
                  onClick={() => removeAt("banned_phrases", idx)}
                  data-testid={`playbook-banned-remove-${idx}`}
                  className="md:col-span-1 inline-flex items-center justify-center border border-red-200 text-red-600 hover:bg-red-50"
                  title="Remove"
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "rules" && (
          <div className="space-y-3" data-testid="playbook-rules-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-navy/70">Editorial rules the LLM must follow. Scope narrows a rule to a specific agent (default: all agents).</p>
              <button onClick={addRule} data-testid="playbook-add-rule" className="inline-flex items-center gap-2 bg-ember text-white px-3 py-1.5 text-xs hover:bg-ember/90">
                <Plus size={14} /> Add rule
              </button>
            </div>
            {rules.length === 0 && <Empty label="No writing rules yet — click Add rule" />}
            {rules.map((r, idx) => (
              <div key={idx} className="bg-white border border-black/10 p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                <textarea
                  data-testid={`playbook-rule-text-${idx}`}
                  rows={2}
                  className={inputCls + " md:col-span-9"}
                  value={r.rule || ""}
                  onChange={(e) => updateList("writing_rules", idx, { rule: e.target.value })}
                  placeholder="e.g. When opening a country for the first time, lead with 'The {country} seat is open.'"
                />
                <select
                  data-testid={`playbook-rule-scope-${idx}`}
                  className={selectCls + " md:col-span-2"}
                  value={r.scope || "all"}
                  onChange={(e) => updateList("writing_rules", idx, { scope: e.target.value })}
                >
                  {SCOPES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
                <button
                  onClick={() => removeAt("writing_rules", idx)}
                  data-testid={`playbook-rule-remove-${idx}`}
                  className="md:col-span-1 inline-flex items-center justify-center border border-red-200 text-red-600 hover:bg-red-50"
                  title="Remove"
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "insights" && (
          <div className="space-y-3" data-testid="playbook-insights-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-navy/70">Patterns we noticed in competitor networks. Borrow (imitate) or Avoid (never do).</p>
              <button onClick={addInsight} data-testid="playbook-add-insight" className="inline-flex items-center gap-2 bg-ember text-white px-3 py-1.5 text-xs hover:bg-ember/90">
                <Plus size={14} /> Add insight
              </button>
            </div>
            {insights.length === 0 && <Empty label="No competitor insights yet — click Add insight" />}
            {insights.map((i, idx) => (
              <div key={idx} className="bg-white border border-black/10 p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                <input
                  data-testid={`playbook-insight-competitor-${idx}`}
                  className={inputCls + " md:col-span-3"}
                  value={i.competitor || ""}
                  onChange={(e) => updateList("competitor_insights", idx, { competitor: e.target.value })}
                  placeholder="e.g. C2C Network"
                />
                <input
                  data-testid={`playbook-insight-pattern-${idx}`}
                  className={inputCls + " md:col-span-6"}
                  value={i.pattern || ""}
                  onChange={(e) => updateList("competitor_insights", idx, { pattern: e.target.value })}
                  placeholder="Pattern description"
                />
                <select
                  data-testid={`playbook-insight-verdict-${idx}`}
                  className={selectCls + " md:col-span-2"}
                  value={i.verdict || "avoid"}
                  onChange={(e) => updateList("competitor_insights", idx, { verdict: e.target.value })}
                >
                  {VERDICTS.map((v) => <option key={v.v} value={v.v}>{v.l}</option>)}
                </select>
                <button
                  onClick={() => removeAt("competitor_insights", idx)}
                  data-testid={`playbook-insight-remove-${idx}`}
                  className="md:col-span-1 inline-flex items-center justify-center border border-red-200 text-red-600 hover:bg-red-50"
                  title="Remove"
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "activities" && (
          <div className="space-y-6" data-testid="playbook-activities-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-navy/70">
                The canonical list of network activities EPAS runs (or should run), derived from every competitor email analysed. Grouped by category. Status: <span className="text-emerald-700 font-medium">active</span> = shipped · <span className="text-amber-700 font-medium">planned</span> = build next · <span className="text-navy/60 font-medium">considered</span> = maybe later · <span className="text-red-600 font-medium">avoid</span> = documented anti-pattern.
              </p>
              <button onClick={addActivity} data-testid="playbook-add-activity" className="inline-flex items-center gap-2 bg-ember text-white px-3 py-1.5 text-xs hover:bg-ember/90">
                <Plus size={14} /> Add activity
              </button>
            </div>
            {activities.length === 0 && <Empty label="No activities yet — click Add activity" />}
            {ACTIVITY_CATEGORIES.map((cat) => {
              const inCat = activities.map((a, idx) => ({ a, idx })).filter(({ a }) => (a.category || "").trim() === cat);
              if (inCat.length === 0) return null;
              return (
                <div key={cat} data-testid={`activity-category-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="overline mb-3 text-navy font-semibold border-l-2 border-ember pl-3">{cat} <span className="text-navy/40 font-normal">· {inCat.length}</span></div>
                  <div className="space-y-3">
                    {inCat.map(({ a, idx }) => (
                      <div key={idx} className="bg-white border border-black/10 p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                        <div className="md:col-span-12 flex items-start justify-between gap-3">
                          <input
                            data-testid={`playbook-activity-name-${idx}`}
                            className={inputCls + " font-semibold text-navy"}
                            value={a.name || ""}
                            onChange={(e) => updateList("activities", idx, { name: e.target.value })}
                            placeholder="Activity name (e.g. Country-First Territory Opening)"
                          />
                          <select
                            data-testid={`playbook-activity-status-${idx}`}
                            className={selectCls + " w-40 flex-shrink-0"}
                            value={a.status || "considered"}
                            onChange={(e) => updateList("activities", idx, { status: e.target.value })}
                          >
                            {ACTIVITY_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                          </select>
                          <button
                            onClick={() => removeAt("activities", idx)}
                            data-testid={`playbook-activity-remove-${idx}`}
                            className="inline-flex items-center justify-center border border-red-200 text-red-600 hover:bg-red-50 px-2 flex-shrink-0"
                            title="Remove"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                        <select
                          data-testid={`playbook-activity-category-${idx}`}
                          className={selectCls + " md:col-span-4"}
                          value={a.category || "Meetings"}
                          onChange={(e) => updateList("activities", idx, { category: e.target.value })}
                        >
                          {ACTIVITY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input
                          data-testid={`playbook-activity-cadence-${idx}`}
                          className={inputCls + " md:col-span-4"}
                          value={a.cadence || ""}
                          onChange={(e) => updateList("activities", idx, { cadence: e.target.value })}
                          placeholder="Cadence (monthly · quarterly · per-country · ad-hoc)"
                        />
                        <input
                          data-testid={`playbook-activity-source-${idx}`}
                          className={inputCls + " md:col-span-4"}
                          value={a.source || ""}
                          onChange={(e) => updateList("activities", idx, { source: e.target.value })}
                          placeholder="Source / inspiration (e.g. Orvion, X2 Events)"
                        />
                        <textarea
                          data-testid={`playbook-activity-description-${idx}`}
                          rows={2}
                          className={inputCls + " md:col-span-6"}
                          value={a.description || ""}
                          onChange={(e) => updateList("activities", idx, { description: e.target.value })}
                          placeholder="What this activity is and what it produces"
                        />
                        <textarea
                          data-testid={`playbook-activity-epas-${idx}`}
                          rows={2}
                          className={inputCls + " md:col-span-6"}
                          value={a.epas_notes || ""}
                          onChange={(e) => updateList("activities", idx, { epas_notes: e.target.value })}
                          placeholder="EPAS-specific notes (how we should handle it)"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "notes" && (
          <div className="space-y-3" data-testid="playbook-notes-section">
            <p className="text-sm text-navy/70">Free-form operator notes appended to every drafting agent&apos;s system prompt. Keep it short — this is context, not instructions.</p>
            <textarea
              data-testid="playbook-custom-notes"
              rows={16}
              className={inputCls}
              value={pb.custom_notes || ""}
              onChange={(e) => setPb({ ...pb, custom_notes: e.target.value })}
              placeholder="e.g. This quarter, prioritise Türkiye and Balkan corridors. Reference the port of Koper only if the recipient is in CEE."
            />
            <div className="text-xs text-navy/50 text-right tabular-nums">
              {(pb.custom_notes || "").length} chars (max 2000 will be sent to the LLM)
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
